import { Flex } from "antd";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  EditIcon,
} from "./style";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { getFoundReportVersionAsyncThunk } from "../../../redux/features/foundReport/foundReportAsyncThunk";
import { clearVersionData } from "../../../redux/features/report/reportSlice";
import { StyledTabs } from "./style";
import EditReportDetailsModal from "../Modal/EditReportDetailsModal";
import { FoundReport, FoundStatuses } from "../../../types/FoundItemTypes";
import { fetchAllFoundItemLogs } from "../../../redux/features/activityLog/activityLogAsyncThunk";
import FoundReportContents from "./FoundReportContents";

const FoundReportDetailsBody: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: report } = useAppSelector((state) => state.foundReport);
  const [activeData, setActiveData] = useState<FoundReport | null>(null);
  const [isEditReportModalOpen, setIsEditReportModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('latest');

  useEffect(() => {
    setActiveData(report);
    if(report?.versionCount && report?.versionCount > 1) {
      setActiveTab('latest');
    }else{
      setActiveTab('original');
    }
  }, [report]);

  const dataToRender = useMemo(() => activeData || report, [activeData, report])

  useEffect(() => {
    if (dataToRender?.id) {
      dispatch(fetchAllFoundItemLogs({ foundId: dataToRender?.id ?? null, withDeleted: dataToRender?.status === FoundStatuses.Discarded }));
    }
  }, [dispatch, dataToRender?.id]);

  const isDiscardedStatus = useMemo(() => dataToRender?.status === FoundStatuses.Discarded, [dataToRender?.status])

  const handleTabChange = async (activeKey: string) => {
    setActiveTab(activeKey);
    if (activeKey.startsWith('version-')) {
      const version = parseInt(activeKey.split('-')[1]);
      if (report?.id) {
        try {
          const response = await dispatch(getFoundReportVersionAsyncThunk({
            trackingId: report.id,
            version,
            withDeleted: dataToRender?.status === FoundStatuses.Discarded
          })).unwrap();
          // Set active data to version data when received
          setActiveData(response);
        } catch (error) {
          console.error('Failed to fetch version:', error);
        }
      }
    } else if (activeKey === 'original' && (report?.versionCount ?? 0) > 1) {
      if (report?.id) {
        try {
          const response = await dispatch(getFoundReportVersionAsyncThunk({
            trackingId: report.id,
            version: 0,
            withDeleted: dataToRender?.status === FoundStatuses.Discarded
          })).unwrap();
          // Set active data to original version
          setActiveData(response);
        } catch (error) {
          console.error('Failed to fetch original version:', error);
        }
      }
    } else if (activeKey === 'latest') {
      // Set active data back to current report for latest tab
      setActiveData(report);
    } else if (activeKey === 'pending') {
      // Keep current report data for pending tab
      setActiveData(report);
    }

    // Clear version data when switching to non-version tabs
    if (!activeKey.startsWith('version-') && activeKey !== 'original') {
      dispatch(clearVersionData());
    }
  };

  const renderVersionTabs = useCallback(() => {
    const tabItems = [];
    const hasLatestTab = (dataToRender?.versionCount ?? 0) > 1;

    // Define showEditButtons here
    const showEditButtons = (tabType: string) => {
      return tabType === 'latest' ||
        (tabType === 'original' && (dataToRender?.versionCount ?? 0) <= 1);
    };

    if ((dataToRender?.versionCount ?? 0) > 1) {
      tabItems.push({
        key: 'latest',
        label: (
          <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            <span>Latest</span>
          </Flex>
        ),
        children: <FoundReportContents showUpdated={true} tabType="latest" activeData={activeData} />,
      });

      if (dataToRender?.versionCount && dataToRender?.versionCount > 1) {
        for (let i = dataToRender?.versionCount - 2; i > 0; i--) {
          tabItems.push({
            key: `version-${i}`,
            label: `Version ${i}`,
            children: <FoundReportContents showUpdated={true} tabType="version" activeData={activeData} />
          });
        }
      }
    }

    tabItems.push({
      key: 'original',
      label: (
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <span>Original</span>
        </Flex>
      ),
      children: <FoundReportContents showUpdated={false} tabType="original" activeData={activeData} />
    });

    if (!dataToRender?.versionCount) {
      return <FoundReportContents showUpdated={false} tabType="original" activeData={activeData} />
    }

    // Use the stored activeTab state instead of recalculating
    const defaultActiveTab = hasLatestTab ? 'latest' : 'original';

    return (
      <>
        <StyledTabs
          type="card"
          activeKey={activeTab}
          defaultActiveKey={defaultActiveTab}
          onChange={handleTabChange}
          items={tabItems}
          tabBarExtraContent={{
            right: showEditButtons(activeTab) && !isDiscardedStatus && (
              <EditIcon onClick={() => setIsEditReportModalOpen(true)} />
            )
          }}
        />
        {isEditReportModalOpen && <EditReportDetailsModal
          isModalOpen={isEditReportModalOpen}
          handleOk={() => setIsEditReportModalOpen(false)}
          handleCancel={() => setIsEditReportModalOpen(false)}
        />}
      </>
    );
  }, [dataToRender, activeData, isEditReportModalOpen]);

  return (
    <div>
      {renderVersionTabs()}
    </div>
  );
};

export default FoundReportDetailsBody;
